export interface TypedAddEventListenerConfig extends AddEventListenerOptions {
  getIsReady: () => boolean;
}

export type TypedAddEventListenerResult = () => void;
export type TypedAddEventListenerResultWithReady = [remove: () => void, ready: () => void];

export class TypedEventTarget<
  EM extends { [k in string]: any } = { [k in string]: any },
> extends EventTarget {
  private removeEventListeners: Set<() => void> = new Set();
  addEventListener(
    type: '*',
    callback: ((event: CustomEventInit<any>) => void) | null,
    options: TypedAddEventListenerConfig
  ): TypedAddEventListenerResultWithReady;
  addEventListener(
    type: '*',
    callback: ((event: CustomEventInit<any>) => void) | null,
    options: AddEventListenerOptions
  ): TypedAddEventListenerResult;
  addEventListener(
    type: '*',
    callback: ((event: CustomEventInit<any>) => void) | null,
    options: boolean
  ): TypedAddEventListenerResult;
  addEventListener(
    type: '*',
    callback: ((event: CustomEventInit<any>) => void) | null
  ): TypedAddEventListenerResult;

  addEventListener<E extends keyof EM>(
    type: E,
    callback: ((event: CustomEventInit<EM[E]>) => void) | null,
    options: TypedAddEventListenerConfig
  ): TypedAddEventListenerResultWithReady;
  addEventListener<E extends keyof EM>(
    type: E,
    callback: ((event: CustomEventInit<EM[E]>) => void) | null,
    options: AddEventListenerOptions
  ): TypedAddEventListenerResult;
  addEventListener<E extends keyof EM>(
    type: E,
    callback: ((event: CustomEventInit<EM[E]>) => void) | null,
    options: boolean
  ): TypedAddEventListenerResult;
  addEventListener<E extends keyof EM>(
    type: E,
    callback: ((event: CustomEventInit<EM[E]>) => void) | null
  ): TypedAddEventListenerResult;

  addEventListener(
    type: string,
    callback: ((e: Event) => void) | null,
    options: TypedAddEventListenerConfig
  ): TypedAddEventListenerResultWithReady;
  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: AddEventListenerOptions
  ): TypedAddEventListenerResult;
  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options: boolean
  ): TypedAddEventListenerResult;
  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null
  ): TypedAddEventListenerResult;

  addEventListener(
    type: string,
    callback: ((e: Event) => void) | null,
    options?: Partial<TypedAddEventListenerConfig> | boolean
  ) {
    let cb: (e: Event) => void;
    let result: TypedAddEventListenerResultWithReady | TypedAddEventListenerResult;

    const remove = () => {
      super.removeEventListener(type, cb, options);
      this.removeEventListeners.delete(remove);
    };

    const getIsReady = typeof options === 'object' ? options.getIsReady : undefined;

    if (getIsReady) {
      let isReady: boolean = false;
      let queue: any[] = [];

      const ready = () => {
        isReady = true;
        queue.forEach((e) => cb(e));
        queue = undefined as any;
      };

      cb = (e: Event) => {
        isReady = isReady || getIsReady();
        if (isReady) {
          return callback?.(e);
        } else {
          queue.push(e);
        }
      };

      result = [remove, ready];
    } else {
      cb = (e: Event) => {
        callback?.(e);
      };

      result = remove;
    }

    this.removeEventListeners.add(remove);

    super.addEventListener(type, cb, options);

    return result;
  }
  dispatchEvent(event: Event): boolean;
  dispatchEvent<E extends keyof EM>(type: E, detail?: EM[E]): boolean;
  dispatchEvent(event: any, detail?: any) {
    const event_ =
      event instanceof Event
        ? event
        : new CustomEvent(event, {
            detail,
          });

    super.dispatchEvent(
      new CustomEvent('*', {
        detail: event_,
      })
    );

    return super.dispatchEvent(event_);
  }
  /**
   * @deprecated use addEventListener return value to remove listener
   */
  removeEventListener(): void {
    throw new Error('Not implemented.');
  }
  removeAllEventListeners() {
    this.removeEventListeners.forEach((remove) => remove());
    this.removeEventListeners.clear();
  }
}
